import * as Dfns from 'date-fns/fp';
import {
  DATE_FORMAT,
  DimensionKey,
  PLATFORM_DISPLAY,
  PacingItem,
  PacingSubData,
  SimplicityKey,
  SummaryData
} from './streaming-pacing-types';

export interface Totals {
  booked: number;
  cleared: number;
  projection: number;
}

export type LineItem = PacingItem | (SummaryData & { independent?: true });

// Keeps only the listed keys the object actually has.
export const pick = <T extends object, K extends keyof T>(
  obj: T | undefined,
  keys: readonly K[]
): Partial<Pick<T, K>> => {
  const picked: Partial<Pick<T, K>> = {};
  keys.forEach(key => {
    if (obj && key in obj) {
      picked[key] = obj[key];
    }
  });
  return picked;
};

// Adds two objects of the same shape number by number, so a field added to the totals is summed
// without being listed here.
const sumTotals = <T extends object>(a: T, b: T): T =>
  Object.fromEntries(
    Object.entries(a).map(([key, value]) => {
      const other: unknown = b[key as keyof T];
      return [
        key,
        typeof value === 'number' ? value + Number(other) : sumTotals(value, other as object)
      ];
    })
  ) as T;

// Sums the booked/cleared/projection totals of every item into a single summary line.
export const mergeItems = (items: PacingSubData[]) =>
  items.length
    ? items
        .map(({ booked, cleared, projection }) => ({ booked, cleared, projection }))
        .reduce((agg, elem) => ({
          booked: sumTotals(agg.booked, elem.booked),
          cleared: sumTotals(agg.cleared, elem.cleared),
          projection: sumTotals(agg.projection, elem.projection)
        }))
    : null;

export const defaultDates = (platform: string, now = new Date()) =>
  platform === PLATFORM_DISPLAY
    ? {
        start: Dfns.format(DATE_FORMAT, Dfns.startOfMonth(now)),
        end: Dfns.format(DATE_FORMAT, Dfns.endOfMonth(now))
      }
    : {
        start: Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(now)),
        end: Dfns.format(DATE_FORMAT, Dfns.addDays(6, Dfns.startOfISOWeek(now)))
      };

// Nothing is booked, so there is no meaningful percentage to show.
export const formatPacingPercent = (booked: number, projection: number) =>
  booked ? `${Math.round((projection / booked) * 100)}%` : '—';

export const itemTotals = (
  item: LineItem,
  simplicityKey: SimplicityKey,
  dimensionKey: DimensionKey
): Totals => ({
  booked: item.booked?.[simplicityKey]?.[dimensionKey] ?? 0,
  cleared: item.cleared?.[simplicityKey]?.[dimensionKey] ?? 0,
  projection: item.projection?.[simplicityKey]?.[dimensionKey] ?? 0
});

// Orders by how far each item is from its booked amount (largest first) and returns the largest
// booked/cleared/projection value, which the bars share as their scale.
export const sortByPacingGap = <T extends PacingItem>(
  items: T[],
  simplicityKey: SimplicityKey,
  dimensionKey: DimensionKey
): { sorted: T[]; max: number } => {
  let max = 0;
  items.forEach(item => {
    const { booked, cleared, projection } = itemTotals(item, simplicityKey, dimensionKey);
    max = Math.max(max, booked, cleared, projection);
  });
  const gap = (item: T) => {
    const { booked, projection } = itemTotals(item, simplicityKey, dimensionKey);
    return Math.abs(booked - projection);
  };
  return { sorted: [...items].sort((a, b) => gap(b) - gap(a)), max };
};

// The unique combinations of the filterable fields, which is all the filter bar needs to build
// its options.
export const filterLines = (rows: object[], fields: string[]): object[] => {
  const seen = new Set<string>();
  return rows
    .map(row => pick(row as Record<string, unknown>, fields))
    .filter(line => {
      const id = JSON.stringify(fields.map(field => line[field]));
      return !seen.has(id) && seen.add(id);
    });
};

// Whether the item carries a pacing alert for the selected simplicity and dimension.
export const hasPacingAlert = (
  item: LineItem,
  simplicityKey: SimplicityKey,
  dimensionKey: DimensionKey
) => 'alert' in item && item.alert?.[simplicityKey]?.[dimensionKey] != null;

export interface ChartRow {
  date: string;
  value: number;
  ideal: number;
  sum?: number;
  idealSum?: number;
}

// One row per day of the range, with the running totals only up to today.
export const buildChartRows = (
  days: NonNullable<PacingSubData['days']>,
  start: string,
  rangeCount: number,
  simplicityKey: SimplicityKey,
  dimensionKey: DimensionKey,
  now = new Date()
): ChartRow[] => {
  let sum = 0;
  let idealSum = 0;
  return Array.from({ length: rangeCount }, (_, i) => {
    const date = Dfns.addDays(i, Dfns.parseISO(start));
    const formatted = Dfns.format(DATE_FORMAT, date);
    const day = days[formatted]?.[simplicityKey];
    const value = day?.[dimensionKey] ?? 0;
    const ideal = day?.ideal[dimensionKey] ?? 0;
    sum += value;
    idealSum += ideal;
    const isPastOrToday = !Dfns.isAfter(now, date);
    return {
      date: formatted,
      value,
      ideal,
      ...(isPastOrToday ? { sum, idealSum } : {})
    };
  });
};

// For the current full month, how much per day has been spent and how much per day is still
// needed to hit the booked spend.
export const computeMonthSummary = (
  summaryData: SummaryData | undefined,
  simplicityKey: SimplicityKey | undefined,
  today = new Date()
) => {
  if (
    !simplicityKey ||
    !summaryData ||
    summaryData.start !== Dfns.format(DATE_FORMAT, Dfns.startOfMonth(today)) ||
    summaryData.end !== Dfns.format(DATE_FORMAT, Dfns.endOfMonth(today)) ||
    today.getDate() <= 1
  ) {
    return undefined;
  }
  const day = today.getDate();
  const daysInMonth = Dfns.endOfMonth(today).getDate();
  const remainingSpend =
    summaryData.booked[simplicityKey].spend - summaryData.cleared[simplicityKey].spend;
  return {
    averageDailySpend: summaryData.cleared[simplicityKey].spend / (day - 1),
    totalSpendRequiredToHitBudget: remainingSpend,
    dailySpendRequiredToHitBudget: remainingSpend / (daysInMonth - (day - 1))
  };
};
